// useComposerVideo.ts — video phase 2 (2026-09-26): one attached clip in the composer.
//
// Picking a clip starts the work straight away — compress on the device, then upload
// straight to R2 — so that by the time the member has written their post the video is
// usually ready. The composer only needs the finished clip's id to post.
//
//   checking → preparing (compressing, 0..1) → uploading (0..1) → ready  |  error
//
// The encoder library is imported on first use only (see videoPrepare.ts).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PostVideo } from '@/core/models/post';
import {
  videoErrorMessage,
  videoUploadService,
  type VideoLimits,
} from '@/core/services/videoUploadService';
import { isVideoPrepareError, prepareErrorMessage } from './videoPrepareErrors';

export type VideoStage = 'checking' | 'preparing' | 'uploading' | 'ready' | 'error';

export interface ComposerVideo {
  file: File;
  /** Object URL of the picked file, for the preview. Revoked on remove/reset. */
  previewUrl: string;
  stage: VideoStage;
  /** 0..1 within the current stage. */
  progress: number;
  error: string | null;
  /** Set once the server accepted the clip. */
  video: PostVideo | null;
  /** Known once the clip is prepared — decides whether it may be a Short (≤60 s). */
  durationMs: number | null;
  /** Known once the clip is prepared: portrait or square (phone-shaped), the Shorts shape. */
  portrait: boolean | null;
}

export interface UseComposerVideo {
  /** Null until the limits load (or when video is off for this member). */
  limits: VideoLimits | null;
  /** The "Add video" button shows only when the owner has switched video on. */
  available: boolean;
  /** Verified email + confirmed 2FA. */
  canUpload: boolean;
  current: ComposerVideo | null;
  /** True while a clip is attached but not ready — posting must wait. */
  busy: boolean;
  attach: (file: File) => void;
  /** Drop the clip; an unposted clip is discarded server-side too. */
  remove: () => void;
  /** After a successful post: forget the clip WITHOUT discarding it (it is on a post now). */
  reset: () => void;
}

export function useComposerVideo(active: boolean): UseComposerVideo {
  const [limits, setLimits] = useState<VideoLimits | null>(null);
  const [current, setCurrent] = useState<ComposerVideo | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const currentRef = useRef<ComposerVideo | null>(null);
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  useEffect(() => {
    if (!active) return;
    let live = true;
    videoUploadService
      .limits()
      .then((l) => {
        if (live) setLimits(l);
      })
      .catch(() => {
        /* no limits → no video button; posting text and images is unaffected */
      });
    return () => {
      live = false;
    };
  }, [active]);

  // Leaving the page mid-upload stops the work; R2 and the server sweep tidy up.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (currentRef.current) URL.revokeObjectURL(currentRef.current.previewUrl);
    },
    [],
  );

  const patch = useCallback((file: File, next: Partial<ComposerVideo>) => {
    setCurrent((prev) => (prev && prev.file === file ? { ...prev, ...next } : prev));
  }, []);

  const attach = useCallback(
    (file: File) => {
      if (!limits) return;
      abortRef.current?.abort();
      const replaced = currentRef.current;
      if (replaced) {
        URL.revokeObjectURL(replaced.previewUrl);
        // Swapping clips: the one already uploaded would otherwise sit unposted until the sweep.
        if (replaced.video) void videoUploadService.cancel(replaced.video.id);
      }
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setCurrent({
        file,
        previewUrl: URL.createObjectURL(file),
        stage: 'checking',
        progress: 0,
        error: null,
        video: null,
        durationMs: null,
        portrait: null,
      });
      void (async () => {
        try {
          const { prepareVideo } = await import('./videoPrepare');
          const prepared = await prepareVideo(file, limits, {
            signal: ctrl.signal,
            onProgress: (p) => patch(file, { stage: 'preparing', progress: p }),
          });
          patch(file, {
            stage: 'uploading',
            progress: 0,
            durationMs: prepared.durationMs,
            portrait: prepared.height >= prepared.width,
          });
          const video = await videoUploadService.upload(prepared, {
            signal: ctrl.signal,
            onProgress: (p) => patch(file, { progress: p }),
          });
          if (ctrl.signal.aborted) {
            void videoUploadService.cancel(video.id);
            return;
          }
          patch(file, { stage: 'ready', progress: 1, video });
        } catch (e) {
          if (ctrl.signal.aborted) return;
          const error = isVideoPrepareError(e)
            ? prepareErrorMessage(e.reason, limits)
            : videoErrorMessage(e);
          patch(file, { stage: 'error', error });
        }
      })();
    },
    [limits, patch],
  );

  const clear = useCallback((discard: boolean) => {
    abortRef.current?.abort();
    abortRef.current = null;
    const prev = currentRef.current;
    if (prev) {
      URL.revokeObjectURL(prev.previewUrl);
      if (discard && prev.video) void videoUploadService.cancel(prev.video.id);
    }
    setCurrent(null);
  }, []);

  const remove = useCallback(() => clear(true), [clear]);
  const reset = useCallback(() => clear(false), [clear]);

  return {
    limits,
    available: Boolean(limits?.enabled),
    canUpload: Boolean(limits?.canUpload),
    current,
    busy: current !== null && current.stage !== 'ready',
    attach,
    remove,
    reset,
  };
}
